/* eslint import/no-unresolved: 0 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* ===== API ベース ===== */
const RAW_API_BASE: string =
  (process.env.REACT_APP_API_BASE_URL as string) ||
  ((import.meta as any)?.env?.VITE_API_BASE_URL as string) ||
  "";

const API_ADMIN = RAW_API_BASE
  ? `${RAW_API_BASE.replace(/\/+$/, "")}/api/admin`
  : "/api/admin";

type FormState = {
  planName: string;
  createdAt: string; // YYYY-MM-DD or ISO
  userName: string;
  gender: string;
  rating: number;
  recommend: number;
  comment: string;
  status: "公開" | "非公開";
};

const defaultForm: FormState = {
  planName: "",
  createdAt: "",
  userName: "",
  gender: "",
  rating: 3,
  recommend: 3,
  comment: "",
  status: "公開",
};

const ReviewEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // "new" or 数値
  const isNew = id === "new";

  const [form, setForm] = useState<FormState>(defaultForm);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ===== 編集時: 既存データ取得 =====
  useEffect(() => {
    if (isNew || !id) return;
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await fetch(`${API_ADMIN}/reviews/${id}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (abort) return;

        const created =
          data.createdAt || data.date || data.postedAt || "";
        const dateOnly = created
          ? new Date(created).toISOString().slice(0, 10)
          : "";

        setForm({
          planName: data.planName ?? data.plan ?? "",
          createdAt: dateOnly,
          userName: data.userName ?? data.user ?? data.author ?? "",
          gender: data.gender ?? "",
          rating: Number(data.rating ?? data.level ?? 3),
          recommend: Number(
            data.recommend ?? data.rating ?? data.level ?? 3
          ),
          comment: data.comment ?? "",
          status: (data.status as "公開" | "非公開") ?? "公開",
        });
      } catch (e: any) {
        setErr(e?.message || "レビューの取得に失敗しました");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [isNew, id]);

  // ===== 入力変更 =====
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (name === "rating" || name === "recommend") {
      setForm((prev) => ({ ...prev, [name]: Number(value) }));
      return;
    }
    if (name === "status" || name === "gender") {
      setForm((prev) => ({ ...prev, [name]: value as any }));
      return;
    }
    if (name === "createdAt" && type === "date") {
      setForm((prev) => ({ ...prev, createdAt: value }));
      return;
    }
    if (name === "planName" || name === "userName" || name === "comment") {
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ===== 登録 / 更新 =====
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErr(null);

      // createdAt を ISO に変換（サーバ側で使うなら）
      const createdAtIso = form.createdAt
        ? new Date(form.createdAt + "T00:00:00+09:00").toISOString()
        : new Date().toISOString();

      const payload = {
        planName: form.planName,
        createdAt: createdAtIso,
        userName: form.userName,
        gender: form.gender,
        rating: form.rating,
        recommend: form.recommend,
        comment: form.comment,
        status: form.status,
      };

      const url = isNew
        ? `${API_ADMIN}/reviews`
        : `${API_ADMIN}/reviews/${id}`;
      const method = isNew ? "POST" : "PUT";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      navigate("/admin/reviews");
    } catch (e: any) {
      setErr(e?.message || "保存に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 600,
        margin: "48px auto",
        background: "#fff",
        borderRadius: 8,
        padding: 36,
        boxShadow: "0 2px 16px #eee",
      }}
    >
      <h2 style={{ marginBottom: 24 }}>
        {isNew ? "レビュー登録" : "レビュー編集"}
      </h2>

      {err && (
        <div
          style={{
            marginBottom: 16,
            padding: 8,
            borderRadius: 6,
            background: "#ffebee",
            color: "#c62828",
          }}
        >
          {err}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* 購入プラン（商品名） */}
        <div style={{ marginBottom: 20 }}>
          <label>購入プラン（商品名）:</label>
          <br />
          <input
            type="text"
            name="planName"
            value={form.planName}
            onChange={handleChange}
            placeholder="例：ゴールドプラン"
            style={{ width: 300 }}
            required
          />
        </div>

        {/* 投稿日時（日付） */}
        <div style={{ marginBottom: 20 }}>
          <label>投稿日:</label>
          <br />
          <input
            type="date"
            name="createdAt"
            value={form.createdAt}
            onChange={handleChange}
            style={{ width: 180 }}
            required
          />
        </div>

        {/* 投稿者 */}
        <div style={{ marginBottom: 20 }}>
          <label>投稿者:</label>
          <br />
          <input
            type="text"
            name="userName"
            value={form.userName}
            onChange={handleChange}
            placeholder="例：レビュー太郎"
            style={{ width: 300 }}
            required
          />
        </div>

        {/* 性別 */}
        <div style={{ marginBottom: 20 }}>
          <label>性別：</label>
          <label style={{ marginLeft: 10 }}>
            <input
              type="radio"
              name="gender"
              value="男性"
              checked={form.gender === "男性"}
              onChange={handleChange}
            />{" "}
            男性
          </label>
          <label style={{ marginLeft: 20 }}>
            <input
              type="radio"
              name="gender"
              value="女性"
              checked={form.gender === "女性"}
              onChange={handleChange}
            />{" "}
            女性
          </label>
        </div>

        {/* おすすめレベル */}
        <div style={{ marginBottom: 20 }}>
          <label>おすすめレベル：</label>
          <br />
          <select
            name="rating"
            value={form.rating}
            onChange={handleChange}
          >
            <option value={5}>★★★★★</option>
            <option value={4}>★★★★</option>
            <option value={3}>★★★</option>
            <option value={2}>★★</option>
            <option value={1}>★</option>
          </select>
        </div>

        {/* コメント */}
        <div style={{ marginBottom: 20 }}>
          <label>コメント：</label>
          <br />
          <textarea
            name="comment"
            value={form.comment}
            onChange={handleChange}
            rows={3}
            placeholder="プランの感想や評価を記入"
            style={{ width: "100%" }}
          />
        </div>

        {/* 公開/非公開 */}
        <div style={{ marginBottom: 20 }}>
          <label>状態：</label>
          <label style={{ marginLeft: 10 }}>
            <input
              type="radio"
              name="status"
              value="公開"
              checked={form.status === "公開"}
              onChange={handleChange}
            />{" "}
            公開
          </label>
          <label style={{ marginLeft: 20 }}>
            <input
              type="radio"
              name="status"
              value="非公開"
              checked={form.status === "非公開"}
              onChange={handleChange}
            />{" "}
            非公開
          </label>
        </div>

        {/* ボタン */}
        <div style={{ marginTop: 28 }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "8px 32px",
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              marginRight: 12,
              cursor: "pointer",
            }}
          >
            {isNew ? "登録" : "更新"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/reviews")}
            disabled={loading}
          >
            一覧に戻る
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewEdit;
